/** Password input with an eye toggle to reveal / hide the typed value.
 *
 * Drop-in replacement for `<input type="password" className="input" ... />`.
 * Works with both react-hook-form (`{...register(...)}` spread) and
 * controlled inputs (`value` + `onChange`).
 *
 * The eye button is `tabIndex={-1}` so it doesn't intercept the form's
 * tab order — Tab still jumps to the next field after the password.
 *
 * Inline SVG icons → no new dependency. Matches the rest of the SPA's
 * Feather-style stroke iconography.
 */
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";


type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;


export const PasswordInput = forwardRef<HTMLInputElement, Props>(
  function PasswordInput({ className = "", ...rest }, ref) {
    const [show, setShow] = useState(false);
    const { t } = useTranslation();
    // Always prepend `input pr-10`; the caller's className is appended
    // verbatim so layout extensions like `w-full` keep working.
    const merged = `input pr-10 ${className}`.trim();
    return (
      <div className="relative">
        <input
          ref={ref}
          type={show ? "text" : "password"}
          className={merged}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={
            show
              ? t("auth.hidePassword", "Hide password")
              : t("auth.showPassword", "Show password")
          }
          aria-pressed={show}
          tabIndex={-1}
          className="absolute inset-y-0 right-2 flex items-center text-nb-text-2 hover:text-nb-text"
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    );
  },
);


function EyeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}


function EyeOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A10.93 10.93 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
